import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getSubscriptionState, isAdmin } from '@/utils/subscription';
import ProPaywallModal from '@/components/ProPaywallModal';

export default function SubscriptionGuard({ children }) {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);

  const returnUrl = location.pathname + location.search;

  const checkAccess = async () => {
    if (location.pathname === '/subscribe' || location.pathname === '/authentication') {
      setHasAccess(true);
      setLoading(false);
      return;
    }

    setLoading(true);

    if (!isAuthenticated || !user) {
      navigate(`/authentication?redirect=${encodeURIComponent(returnUrl)}`, { replace: true });
      return;
    }

    if (isAdmin(user)) {
      setHasAccess(true);
      setShowPaywall(false);
      setLoading(false);
      return;
    }

    try {
      const subscriptionState = await getSubscriptionState(user);
      if (subscriptionState.isActive) {
        setHasAccess(true);
        setShowPaywall(false);
      } else {
        setHasAccess(false);
        setShowPaywall(true);
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
      setHasAccess(false);
      setShowPaywall(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    (async () => {
      if (!isMounted) return;
      await checkAccess();
    })();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAuthenticated, location.pathname, location.search]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream-paper flex items-center justify-center">
        <div className="text-forest-ink">Checking access…</div>
      </div>
    );
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-cream-paper">
      <ProPaywallModal
        open={showPaywall}
        returnPath={returnUrl}
        onClose={() => navigate('/')}
        onUnlocked={checkAccess}
      />
    </div>
  );
}
